import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import {
  getDashboard,
  getMicroLoanEligibility,
  getOnboarding,
  type PortalMicroLoanEligibility,
} from "../api/endpoints/portal"
import { Skeleton } from "../components/skeleton"
import { formatCurrency, formatCurrencyBreakdown, formatDate, formatDateTime } from "../utils/formatting"

function statusTone(label: string) {
  if (label === "SERIOUSLY_OVERDUE") return "danger"
  if (label === "OVERDUE") return "warning"
  if (label === "PAYMENT_DUE_SOON") return "neutral"
  return "success"
}

const QUICK_LOAN_HIDDEN_REASONS = new Set(["MICRO_LOAN_DISABLED", "MICRO_LOAN_PORTAL_DISABLED"])

function quickLoanReasonLabel(code: string) {
  switch (code) {
    case "CLIENT_INACTIVE":
      return "Your account is inactive."
    case "KYC_NOT_APPROVED":
      return "Your KYC review must be approved first."
    case "PHONE_NOT_VERIFIED":
      return "Verify your phone number before requesting a quick loan."
    case "NATIONAL_ID_REQUIRED":
      return "Complete your national ID details first."
    case "CLIENT_TOO_YOUNG":
      return "You do not meet the minimum age requirement."
    case "MICRO_LOAN_TIER_FROZEN":
      return "Your quick-loan tier is temporarily frozen."
    case "STANDARD_LOAN_OVERDUE":
      return "Resolve your overdue standard loan first."
    case "ACTIVE_MICRO_LOAN_EXISTS":
      return "You already have an active quick loan."
    case "EMPLOYMENT_VERIFICATION_REQUIRED":
      return "A verified proof-of-income document is required."
    default:
      return code.replaceAll("_", " ")
  }
}

function shouldHideQuickLoanEntry(eligibility: PortalMicroLoanEligibility | null | undefined) {
  if (!eligibility) {
    return true
  }

  return eligibility.reasons.some((reason) => QUICK_LOAN_HIDDEN_REASONS.has(reason))
}

function QuickLoanTierProgressCard({
  eligibility,
}: {
  eligibility: PortalMicroLoanEligibility
}) {
  return (
    <section className="card list-card">
      <div>
        <p className="eyebrow">Tier progress</p>
        <h3>
          {eligibility.tier.currentTier} · {eligibility.tier.tierName}
        </h3>
      </div>
      <div className="list-row">
        <div>
          <strong>Current limit</strong>
          <p className="subtle">{eligibility.tier.terms.principalCeilingDisplay}</p>
        </div>
        <div className="align-right">
          <strong>
            {eligibility.tier.tierScore} / {eligibility.tier.terms.requiredCycles}
          </strong>
          <p className="subtle">Completed on-time cycles</p>
        </div>
      </div>
      <div className="list-row">
        <div>
          <strong>Tenor range</strong>
          <p className="subtle">
            {eligibility.tier.terms.minTenorDays}-{eligibility.tier.terms.maxTenorDays} days
          </p>
        </div>
        <div className="align-right">
          <strong>{Math.round(eligibility.tier.terms.interestRate * 100)}% interest</strong>
          <p className="subtle">
            {Math.round(eligibility.tier.terms.applicationFeeRate * 100)}% application fee
          </p>
        </div>
      </div>
      <p className="subtle">
        {eligibility.tier.nextTier
          ? `${eligibility.tier.nextTier.remainingCycles} more on-time closures unlock ${eligibility.tier.nextTier.tierName} and ${eligibility.tier.nextTier.principalCeilingDisplay}.`
          : "You are already at the highest quick-loan tier."}
      </p>
    </section>
  )
}

export function DashboardPage() {
  const navigate = useNavigate()
  const dashboardQuery = useQuery({
    queryKey: ["portal-dashboard"],
    queryFn: () => getDashboard().then((response) => response.data),
  })
  const onboardingQuery = useQuery({
    queryKey: ["portal-onboarding"],
    queryFn: () => getOnboarding().then((response) => response.data),
  })
  const microLoanEligibilityQuery = useQuery({
    queryKey: ["portal-micro-loan-eligibility"],
    queryFn: () => getMicroLoanEligibility().then((response) => response.data),
  })

  if (dashboardQuery.isLoading || onboardingQuery.isLoading) {
    return (
      <div className="page-stack">
        <Skeleton height={96} />
        <Skeleton height={180} />
        <Skeleton height={180} />
      </div>
    )
  }

  const data = dashboardQuery.data
  const onboarding = onboardingQuery.data
  const microLoanEligibility = microLoanEligibilityQuery.data
  if (!data || !onboarding) return null
  const hasLoans = data.summary.totalActiveLoans > 0
  const standardProductsVisible = Boolean(onboarding.portalStandardProductsEnabled)
  const canAccessStandardApplicationPage = Boolean(standardProductsVisible || onboarding.application)
  const hideQuickLoanEntry = shouldHideQuickLoanEntry(microLoanEligibility)
  const showQuickLoanCta = microLoanEligibility
    ? !hideQuickLoanEntry && microLoanEligibility.eligible
    : false
  const showQuickLoanBlockedState =
    microLoanEligibility ? !hideQuickLoanEntry && !microLoanEligibility.eligible : false
  const quickLoanUnavailable = microLoanEligibility ? hideQuickLoanEntry : false
  const standardApplicationLabel = onboarding.application
    ? "View standard application"
    : showQuickLoanCta || showQuickLoanBlockedState
      ? "Other loan products"
      : "Standard application"

  if (!hasLoans) {
    return (
      <div className="page-stack">
        <section className="hero-card">
          <div>
            <p className="eyebrow">Borrower journey</p>
            <h2>
              {onboarding.application?.status === "SUBMITTED"
                ? "Application under review"
                : onboarding.kycStatus === "ACTION_REQUIRED"
                  ? "More KYC information needed"
                  : onboarding.kycStatus === "REJECTED"
                  ? "Action required"
                  : "Start your loan application"}
            </h2>
            <p className="subtle">
              {onboarding.application?.status === "SUBMITTED"
                ? "Your application has been submitted. We will update you once review is complete."
                : onboarding.kycStatus === "ACTION_REQUIRED"
                  ? onboarding.activeKycRequest?.message ??
                    "Update the requested KYC information, then resubmit for review."
                  : onboarding.kycStatus === "REJECTED"
                    ? "Your KYC review was rejected. Contact support if you need clarification."
                  : "Complete onboarding, upload KYC documents, and submit a borrower application."}
            </p>
          </div>
          <div className="hero-metrics">
            <div>
              <strong>{onboarding.isProfileComplete ? "Yes" : "No"}</strong>
              <span>Profile complete</span>
            </div>
            <div>
              <strong>{onboarding.hasRequiredDocuments ? "Yes" : "No"}</strong>
              <span>KYC ready</span>
            </div>
          </div>
        </section>

        <section className="card">
          <div className="button-row">
            {!showQuickLoanCta && canAccessStandardApplicationPage && quickLoanUnavailable ? (
              <button className="primary-button" onClick={() => navigate("/apply")} type="button">
                {standardApplicationLabel}
              </button>
            ) : !showQuickLoanCta ? (
              <button className="primary-button" onClick={() => navigate("/onboarding")} type="button">
                Manage profile and KYC
              </button>
            ) : null}
            {canAccessStandardApplicationPage ? (
              <button className="ghost-button" onClick={() => navigate("/apply")} type="button">
                {standardApplicationLabel}
              </button>
            ) : null}
            <button className="ghost-button" onClick={() => navigate("/onboarding")} type="button">
              {showQuickLoanCta || canAccessStandardApplicationPage ? "Manage profile and KYC" : "Back to onboarding"}
            </button>
          </div>
          {onboarding.missingDocumentTypes.length > 0 ? (
            <div className="hint-copy">
              Missing documents: {onboarding.missingDocumentTypes.join(", ").replaceAll("_", " ")}
            </div>
          ) : null}
        </section>

        <section className="card list-card">
          <div>
            <p className="eyebrow">Status</p>
            <h3>KYC and application</h3>
          </div>
          <div className="list-row">
            <div>
              <strong>KYC status</strong>
              <p className="subtle">{onboarding.kycStatus}</p>
            </div>
          </div>
          {onboarding.activeKycRequest ? (
            <div className="list-row">
              <div>
                <strong>What we need</strong>
                <p className="subtle">{onboarding.activeKycRequest.message}</p>
              </div>
            </div>
          ) : null}
          {onboarding.application ? (
            <div className="list-row">
              <div>
                <strong>Latest application</strong>
                <p className="subtle">
                  {onboarding.application.product.name} · {onboarding.application.status}
                </p>
              </div>
            </div>
          ) : null}
        </section>

        {showQuickLoanCta && microLoanEligibility ? (
          <section className="card action-card">
            <div>
              <p className="eyebrow">Quick loan</p>
              <h3>{microLoanEligibility.tier.terms.principalCeilingDisplay}</h3>
              <p className="subtle">
                You are eligible under {microLoanEligibility.tier.tierName}. Request a quick loan from the dashboard.
              </p>
            </div>
            <button className="primary-button" onClick={() => navigate("/quick-loan")} type="button">
              Request quick loan
            </button>
          </section>
        ) : null}

        {showQuickLoanBlockedState && microLoanEligibility ? (
          <section className="card list-card">
            <div>
              <p className="eyebrow">Quick loan status</p>
              <h3>{microLoanEligibility.tier.tierName}</h3>
            </div>
            <div className="list-row">
              <div>
                <strong>Current blocker</strong>
                <p className="subtle">
                  {quickLoanReasonLabel(microLoanEligibility.reasons[0] ?? "NOT_ELIGIBLE")}
                </p>
              </div>
            </div>
            {microLoanEligibility.activeMicroLoan ? (
              <div className="list-row">
                <div>
                  <strong>Active quick loan</strong>
                  <p className="subtle">
                    {microLoanEligibility.activeMicroLoan.loanId} · due{" "}
                    {formatDate(microLoanEligibility.activeMicroLoan.dueDate)}
                  </p>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

      {!hideQuickLoanEntry && microLoanEligibility ? (
        <QuickLoanTierProgressCard eligibility={microLoanEligibility} />
      ) : null}

      {canAccessStandardApplicationPage ? (
        <section className="card action-card">
          <div>
            <p className="eyebrow">Other loan products</p>
            <h3>{onboarding.application ? "Continue your standard application" : "Standard loan options"}</h3>
            <p className="subtle">
              Standard loan products are available to your account as a secondary path from the borrower portal.
            </p>
          </div>
          <button className="ghost-button" onClick={() => navigate("/apply")} type="button">
            {standardApplicationLabel}
          </button>
        </section>
      ) : null}
    </div>
  )
}

  return (
    <div className="page-stack">
      <section className="hero-card">
        <div>
          <p className="eyebrow">Overview</p>
          <h2>{formatCurrencyBreakdown(data.summary.totalOutstandingBalanceByCurrency)}</h2>
          <p className="subtle">Total outstanding across your active loans</p>
        </div>
        <div className="hero-metrics">
          <div>
            <strong>{data.summary.totalActiveLoans}</strong>
            <span>Active loans</span>
          </div>
          <div>
            <strong>{data.summary.paymentStreak}</strong>
            <span>On-time streak</span>
          </div>
        </div>
      </section>

      {dashboardQuery.dataUpdatedAt ? (
        <div className="last-updated">
          Last updated {formatDateTime(new Date(dashboardQuery.dataUpdatedAt).toISOString())}
        </div>
      ) : null}

      {data.nextPayment ? (
        <section className="card action-card">
          <div>
            <p className="eyebrow">Next payment</p>
            <h3>{formatCurrency(data.nextPayment.currency, data.nextPayment.amount)}</h3>
            <p className="subtle">Due {formatDate(data.nextPayment.dueDate)}</p>
          </div>
          <button
            className="primary-button"
            onClick={() => navigate(`/payments?loanId=${data.nextPayment?.loanId}`)}
            type="button"
          >
            Pay now
          </button>
        </section>
      ) : null}

      {showQuickLoanCta && microLoanEligibility ? (
        <section className="card action-card">
          <div>
            <p className="eyebrow">Quick loan</p>
            <h3>{microLoanEligibility.tier.terms.principalCeilingDisplay}</h3>
            <p className="subtle">
              Available under {microLoanEligibility.tier.tierName}. Net disbursement is shown before you submit.
            </p>
          </div>
          <button className="primary-button" onClick={() => navigate("/quick-loan")} type="button">
            Request quick loan
          </button>
        </section>
      ) : null}

      {showQuickLoanBlockedState && microLoanEligibility ? (
        <section className="card list-card">
          <div>
            <p className="eyebrow">Quick loan status</p>
            <h3>Not currently eligible</h3>
          </div>
          <div className="list-row">
            <div>
              <strong>Main blocker</strong>
              <p className="subtle">
                {quickLoanReasonLabel(microLoanEligibility.reasons[0] ?? "NOT_ELIGIBLE")}
              </p>
            </div>
          </div>
          {microLoanEligibility.activeMicroLoan ? (
            <div className="list-row">
              <div>
                <strong>Active quick loan</strong>
                <p className="subtle">
                  {microLoanEligibility.activeMicroLoan.loanId} ·{" "}
                  {formatCurrency("USD", microLoanEligibility.activeMicroLoan.principalAmount)}
                </p>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {!hideQuickLoanEntry && microLoanEligibility ? (
        <QuickLoanTierProgressCard eligibility={microLoanEligibility} />
      ) : null}

      <section className="section-header">
        <div>
          <p className="eyebrow">Your loans</p>
          <h3>Active accounts</h3>
        </div>
      </section>

      {data.loans.map((loan) => (
        <article className="card loan-card" key={loan.loanId}>
          <div className="loan-card-header">
            <div>
              <p className="mono">{loan.loanId}</p>
              <h4>{loan.productName}</h4>
            </div>
            <span className={`status-pill ${statusTone(loan.overdueLabel)}`}>
              {loan.overdueLabel.replaceAll("_", " ")}
            </span>
          </div>
          <dl className="metric-grid">
            <div>
              <dt>Outstanding</dt>
              <dd>{formatCurrency(loan.currency, loan.outstandingBalance)}</dd>
            </div>
            <div>
              <dt>Due date</dt>
              <dd>{formatDate(loan.dueDate)}</dd>
            </div>
            <div>
              <dt>Paid so far</dt>
              <dd>{formatCurrency(loan.currency, loan.totalRepaid)}</dd>
            </div>
            <div>
              <dt>Progress</dt>
              <dd>{loan.repaymentProgressPercent}%</dd>
            </div>
            {loan.loanType === "MICRO" ? (
              <div>
                <dt>Net disbursed</dt>
                <dd>{formatCurrency(loan.currency, loan.netDisbursedAmount ?? 0)}</dd>
              </div>
            ) : null}
            {loan.loanType === "MICRO" ? (
              <div>
                <dt>Tier</dt>
                <dd>{loan.microLoanTier ?? "-"}</dd>
              </div>
            ) : null}
          </dl>
          <div className="progress-bar">
            <span style={{ width: `${loan.repaymentProgressPercent}%` }} />
          </div>
          <div className="button-row">
            <button className="primary-button" onClick={() => navigate(`/payments?loanId=${loan.loanId}`)} type="button">
              Pay now
            </button>
            <button className="ghost-button" onClick={() => navigate(`/loans/${loan.loanId}`)} type="button">
              View details
            </button>
          </div>
        </article>
      ))}

      <section className="section-header">
        <div>
          <p className="eyebrow">Recent activity</p>
          <h3>Latest transactions</h3>
        </div>
      </section>

      <div className="card list-card">
        {data.recentActivity.length === 0 ? (
          <p className="empty-copy">No repayments have been recorded yet.</p>
        ) : (
          data.recentActivity.map((payment) => (
            <div className="list-row" key={payment.paymentId}>
              <div>
                <strong>{formatCurrency(payment.currency, payment.amount)}</strong>
                <p className="subtle">
                  {payment.loanId} · {payment.transactionReference ?? "No reference"}
                </p>
              </div>
              <span className="subtle">{formatDate(payment.paymentDate)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
